/** Pure naming helpers for PDF → image output (unit-tested in Node). */

/** Names for the output folders, one per file, so same-named PDFs never overwrite each other in the ZIP:
 *  ["a","a","b","a"] → ["a","a (2)","b","a (3)"]. Comparison is case-insensitive (ZIP tools on
 *  Windows/macOS treat "A" and "a" as the same folder). */
export function uniqueFolderNames(baseNames: string[]): string[] {
  const seen = new Map<string, number>();
  const used = new Set<string>();
  return baseNames.map((name) => {
    const key = name.toLowerCase();
    let n = (seen.get(key) ?? 0) + 1;
    let candidate = n === 1 ? name : `${name} (${n})`;
    while (used.has(candidate.toLowerCase())) {
      n++;
      candidate = `${name} (${n})`;
    }
    seen.set(key, n);
    used.add(candidate.toLowerCase());
    return candidate;
  });
}

/** "report.PDF" → "report". Characters that are illegal in ZIP/file names on Windows are replaced. */
export function stemForImages(fileName: string): string {
  const stem = fileName.replace(/\.pdf$/i, "").replace(/[\\/:*?"<>|]/g, "_").trim();
  return stem || "document";
}

/** "name-page-03.jpg" (or just "name.jpg" when the PDF has one page). Padding follows the page count. */
export function imageFileName(stem: string, pageNum: number, numPages: number, ext: string): string {
  if (numPages <= 1) return `${stem}.${ext}`;
  const width = Math.max(2, String(numPages).length);
  return `${stem}-page-${String(pageNum).padStart(width, "0")}.${ext}`;
}

/** Removes repeated page numbers, keeping first-seen order ("1,1,3" → 1,3). */
export function uniquePages(pages: number[]): number[] {
  return Array.from(new Set(pages));
}

/** dpi shown on the buttons. The renderer's scale 1 = 72 dpi (one PDF point per pixel). */
export function dpiForScale(scale: number): number {
  return Math.round(scale * 72);
}

export function pagesLabel(n: number): string {
  return `${n} page${n === 1 ? "" : "s"}`;
}
